import { zodResolver } from '@hookform/resolvers/zod';
import { FC, PropsWithChildren, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { selectUserProjectTimeZone } from 'core/userContext';
import { momentZodType } from 'lib/zod';
import { MIN_RECURRING_CAMPAIGN_END_ON_DAYS } from 'modules/campaigns/lib/constants';
import { GraphQLCampaign } from 'modules/campaigns/lib/types';
import { ScheduleType } from 'modules/domains/campaign';

import { TimeIn12HourClock } from 'ui/datePicker';
import { z } from 'zod';
import { useCampaignContext } from '../../../context';
import { EndEligibilityType, ScheduleEligibilityFormState, TimeUnitType } from '../../../types';
import {
  getIsEndAfterValid,
  getIsEndDateAfterStartDateOrNow,
  getIsStartDateInTheFuture,
  getIsStartTimeInTheFuture,
  getIsTimeValid,
  getScheduleFormInitialValues,
} from '../../ScheduleCampaignModal/ScheduleCampaignModal.utils';

/**
 * Root zod schema for schedule eligibility campaign form
 */
export const EligibilityCampaignSchema = z
  .object({
    startDate: momentZodType.optional(),
    startTime: z
      .string()
      .refine(getIsTimeValid, {
        message: 'Time must be valid. 12 and 24-hour formats are supported.',
      })
      .optional(),

    endType: z.nativeEnum(EndEligibilityType),

    endDate: momentZodType.optional(),
    endTime: z.string().optional(),
    endAfterValue: z.coerce.number().optional(),
    endAfterUnit: z.nativeEnum(TimeUnitType),

    projectTimeZone: z.string().optional(),

    /**
     * Note: these values are actually conditionally required.
     * It's enforced via "refinements" method.
     */
    scheduleType: z.nativeEnum(ScheduleType),
    startingTimeZone: z.string().optional(),
    fallbackTimeZone: z.string().optional(),
  })
  .refine(getIsStartDateInTheFuture, {
    path: ['startDate'],
    message: 'Date must be in the future in the selected time zone.',
  })
  .refine(getIsStartTimeInTheFuture, {
    path: ['startTime'],
    message: 'Time must be in the future in the selected time zone.',
  })
  .refine(getIsEndDateAfterStartDateOrNow, {
    path: ['endDate'],
    message: `End date must be at least ${MIN_RECURRING_CAMPAIGN_END_ON_DAYS} day after the start date.`,
  })
  .refine(getIsEndAfterValid, {
    path: ['endAfterValue'],
    message: `End after must be a numberic unit representing time after the start date.`,
  });

const getInitialValues = (campaign: GraphQLCampaign, timeZone: string) => {
  const baseInitialValues = getScheduleFormInitialValues(campaign, timeZone);

  const endTime = baseInitialValues.endDate?.clone()?.format('hh:mm A') as TimeIn12HourClock;

  return {
    ...baseInitialValues,
    endTime,
    endAfterValue: undefined,
    endAfterUnit: TimeUnitType.Days,
    endType: campaign?.schedulerMsgParams?.endDate?.timestamp
      ? EndEligibilityType.OnDate
      : EndEligibilityType.Never,
  };
};

export const DisplayEligibilityCampaignFormProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const projectTimeZone = useSelector(selectUserProjectTimeZone);
  const { campaign } = useCampaignContext();

  const defaultValues = useMemo(() => getInitialValues(campaign, projectTimeZone), []);

  const methods = useForm<ScheduleEligibilityFormState>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(EligibilityCampaignSchema),
    shouldUnregister: true,
  });

  /**
   * Validate default values on mount.
   * This is especially done because `isDirty` is not set to true until the user interacts with the form,
   * but we want to allow users to submit the form with the default values.
   * @see https://github.com/react-hook-form/react-hook-form/issues/2755
   */
  useEffect(() => {
    methods.trigger();
  }, []);

  return <FormProvider {...methods}>{children}</FormProvider>;
};

export const withDisplayEligibilityCampaignFormProvider =
  <DisplayEligibilityCampaignModalProps extends Record<string, any>>(
    Consumer: FC<React.PropsWithChildren<DisplayEligibilityCampaignModalProps>>,
  ) =>
  (props: DisplayEligibilityCampaignModalProps) => {
    return (
      <DisplayEligibilityCampaignFormProvider>
        <Consumer {...props} />
      </DisplayEligibilityCampaignFormProvider>
    );
  };
